import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Play, Users, Camera } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

export const GalleryPage: React.FC = () => {
  const BASE_URL = import.meta.env.BASE_URL;
  const photos = [
    {
      id: 1,
      src: `${BASE_URL}images/hero_banner_1_0.jpg`,
      alt: 'Blood donation banner',
      category: 'Events'
    },
    {
      id: 2,
      src: `${BASE_URL}images/donation_process_1_2.jpg`,
      alt: 'Student donating blood in campus drive',
      category: 'Donation Process'
    },
    {
      id: 3,
      src: `${BASE_URL}images/hero_banner_1_6.png`,
      alt: 'Template banner with donation message',
      category: 'Campaign'
    },
    {
      id: 4,
      src: `${BASE_URL}images/donation_process_1_4.jpg`,
      alt: 'Donor during blood collection process',
      category: 'Donation Process'
    },
    {
      id: 5,
      src: `${BASE_URL}images/hero_banner_2_2.jpg`,
      alt: 'World Blood Donor Day banner',
      category: 'Events'
    },
    {
      id: 6,
      src: `${BASE_URL}images/donation_process_2_2.jpg`,
      alt: 'Three ways your donation helps',
      category: 'Education'
    },
    {
      id: 7,
      src: `${BASE_URL}images/medical_theme_1_7.jpg`,
      alt: 'Medical laboratory professionals',
      category: 'Behind the Scenes'
    },
    {
      id: 8,
      src: `${BASE_URL}images/donation_process_2_4.jpg`,
      alt: 'Donor giving blood at VA facility',
      category: 'Donation Process'
    },
    {
      id: 9,
      src: `${BASE_URL}images/hero_banner_2_6.jpg`,
      alt: 'Clean banner design for donation campaign',
      category: 'Campaign'
    },
    {
      id: 10,
      src: `${BASE_URL}images/medical_theme_2_1.jpg`,
      alt: 'Sterilized medical equipment',
      category: 'Behind the Scenes'
    },
    {
      id: 11,
      src: `${BASE_URL}images/donation_process_2_6.jpg`,
      alt: 'Patient donating blood during shortage',
      category: 'Events'
    },
    {
      id: 12,
      src: `${BASE_URL}images/hero_banner_2_8.jpg`,
      alt: 'Donate blood and save life banner',
      category: 'Campaign'
    }
  ];

  const events = [
    {
      date: '2025-10-15',
      title: 'Community Blood Drive at City Hall',
      description: 'Successful drive with 150 donors participating and collecting 135 units of blood.',
      image: `${BASE_URL}images/hero_banner_2_2.jpg`
    },
    {
      date: '2025-09-28',
      title: 'University Campus Blood Drive',
      description: 'Annual campus drive with over 200 students donating blood for the first time.',
      image: `${BASE_URL}images/donation_process_1_2.jpg`
    },
    {
      date: '2025-08-20',
      title: 'Corporate Blood Donation Event',
      description: 'Local businesses joined together for a blood drive, resulting in 85 units collected.',
      image: `${BASE_URL}images/donation_process_2_2.jpg`
    },
    {
      date: '2025-07-14',
      title: 'World Blood Donor Day Celebration',
      description: 'Special celebration honoring our regular donors with awards and recognition.',
      image: `${BASE_URL}images/hero_banner_1_0.jpg`
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Page Hero */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-title font-bold text-neutral-900 mb-4">Our Community in Action</h1>
          <p className="text-body-lg text-neutral-600 max-w-3xl mx-auto">
            See the faces behind the life-saving work. From donation drives to community events, 
            discover the impact we're making together.
          </p>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Photo Gallery</h2>
            <p className="text-body-lg text-neutral-600">
              Capturing moments of generosity and community spirit
            </p>
          </div>

          {/* Masonry Grid */}
          <div className="columns-1 md:columns-2 lg:columns-3 gap-6">
            {photos.map((photo) => (
              <div key={photo.id} className="break-inside-avoid mb-6">
                <Card hover className="p-0 overflow-hidden">
                  <div className="relative">
                    <img 
                      src={photo.src} 
                      alt={photo.alt} 
                      className="w-full h-auto object-cover hover:scale-105 transition-transform duration-normal"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-small font-medium">
                        {photo.category}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Educational Videos</h2>
            <p className="text-body-lg text-neutral-600">
              Learn about the donation process and hear from our community
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Video 1 */}
            <Card hover className="p-0 overflow-hidden">
              <div className="relative aspect-video">
                <img 
                  src={`${BASE_URL}images/donation_process_1_5.jpg`} 
                  alt="Blood donation process video thumbnail" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                  <button className="w-20 h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all duration-normal">
                    <Play className="h-8 w-8 text-primary-600 ml-1" />
                  </button>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-lg font-semibold text-white mb-2">The Blood Donation Process</h3>
                  <p className="text-small text-gray-200">
                    Step-by-step guide to donating blood, from registration to recovery
                  </p>
                </div>
              </div>
            </Card>

            {/* Video 2 */}
            <Card hover className="p-0 overflow-hidden">
              <div className="relative aspect-video">
                <img 
                  src={`${BASE_URL}images/medical_theme_1_0.png`} 
                  alt="Patient testimonial video thumbnail" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                  <button className="w-20 h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all duration-normal">
                    <Play className="h-8 w-8 text-primary-600 ml-1" />
                  </button>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-lg font-semibold text-white mb-2">Patient Testimonials</h3>
                  <p className="text-small text-gray-200">
                    Hear from patients whose lives were saved through blood donations
                  </p>
                </div>
              </div>
            </Card>

            {/* Video 3 */}
            <Card hover className="p-0 overflow-hidden">
              <div className="relative aspect-video">
                <img 
                  src={`${BASE_URL}images/benefits_icons_1_3.jpeg`} 
                  alt="Donor benefits video thumbnail" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                  <button className="w-20 h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all duration-normal">
                    <Play className="h-8 w-8 text-primary-600 ml-1" />
                  </button>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-lg font-semibold text-white mb-2">Benefits of Donating</h3>
                  <p className="text-small text-gray-200">
                    Learn about the health benefits and personal satisfaction of blood donation
                  </p>
                </div>
              </div>
            </Card>

            {/* Video 4 */}
            <Card hover className="p-0 overflow-hidden">
              <div className="relative aspect-video">
                <img 
                  src={`${BASE_URL}images/medical_theme_2_5.jpg`} 
                  alt="Behind the scenes video thumbnail" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                  <button className="w-20 h-20 bg-white bg-opacity-90 rounded-full flex items-center justify-center hover:bg-opacity-100 transition-all duration-normal">
                    <Play className="h-8 w-8 text-primary-600 ml-1" />
                  </button>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-lg font-semibold text-white mb-2">Behind the Scenes</h3>
                  <p className="text-small text-gray-200">
                    Take a tour of our blood processing facility and meet our dedicated team
                  </p>
                </div>
              </div>
            </Card>
          </div>
          <div className="text-center mt-12">
            <Button variant="outline">
              <Camera className="mr-2 h-5 w-5" />
              View All Videos
            </Button>
          </div>
        </div>
      </section>

      {/* Latest Events */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Latest Events</h2>
            <p className="text-body-lg text-neutral-600">
              Recent blood donation drives and community events
            </p>
          </div>
          <div className="space-y-8">
            {events.map((event, index) => (
              <Card key={index} hover>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-3">
                      <img 
                        src={event.image} 
                        alt={event.title} 
                        className="w-full h-48 lg:h-32 object-cover rounded-lg"
                      />
                    </div>
                    <div className="lg:col-span-9">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center text-primary-600 mb-2">
                            <Calendar className="h-4 w-4 mr-2" />
                            <span className="text-small font-medium">
                              {new Date(event.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </span>
                          </div>
                          <h3 className="text-subtitle font-semibold text-neutral-900 mb-2">
                            {event.title}
                          </h3>
                          <p className="text-body text-neutral-600">
                            {event.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Button>
              <Users className="mr-2 h-5 w-5" />
              View All Events
            </Button>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-subtitle font-bold mb-4">Join Our Next Event</h2>
          <p className="text-body-lg mb-8 text-red-100">
            Be part of our upcoming community blood drives and make a difference in someone's life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donors" state={{ scrollTo: 'register-to-donate' }}>
              <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-100">
                Register as Donor
              </Button>
            </Link>
            <Link to="/contact" state={{ scrollTo: 'contact-form' }}>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary-600">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
